import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import type { DashboardData, DashboardStats, CategoryStats, RecentActivity } from '@/types/dashboard'
import type { FAQCategory, FAQStatus } from '@/types/knowledge'

export function useDashboardData() {
  const [dashboardData, setDashboardData] = useState<DashboardData>({
    stats: {
      totalFAQs: 0,
      activeFAQs: 0,
      pendingReview: 0,
      totalSearchQueries: 0
    },
    categoryBreakdown: [],
    recentActivities: [],
    isLoading: true,
    error: null
  })

  const fetchDashboardStats = async (): Promise<DashboardStats> => {
    // Get total FAQs count
    const { count: totalFAQs, error: totalError } = await supabase
      .from('knowledge')
      .select('*', { count: 'exact', head: true })

    if (totalError) {
      throw new Error(`Failed to fetch total FAQs: ${totalError.message}`)
    }

    // Get active/approved FAQs count
    const { count: activeFAQs, error: activeError } = await supabase
      .from('knowledge')
      .select('*', { count: 'exact', head: true })
      .or('status.eq.approved,status.eq.active')

    if (activeError) {
      throw new Error(`Failed to fetch active FAQs: ${activeError.message}`)
    }

    // Get FAQs pending review
    const { count: pendingReview, error: pendingError } = await supabase
      .from('knowledge')
      .select('*', { count: 'exact', head: true })
      .eq('status', 'review')

    if (pendingError) {
      throw new Error(`Failed to fetch pending review FAQs: ${pendingError.message}`)
    }

    // For search queries, we'll use the usage_count field as a proxy
    // In a real implementation, you'd have a separate search_logs table
    const { data: usageData, error: usageError } = await supabase
      .from('knowledge')
      .select('usage_count')
      .not('usage_count', 'is', null)

    if (usageError) {
      throw new Error(`Failed to fetch search queries: ${usageError.message}`)
    }

    const totalSearchQueries = usageData?.reduce((sum, item) => sum + (item.usage_count || 0), 0) || 0

    return {
      totalFAQs: totalFAQs || 0,
      activeFAQs: activeFAQs || 0,
      pendingReview: pendingReview || 0,
      totalSearchQueries
    }
  }

  const fetchCategoryBreakdown = async (): Promise<CategoryStats[]> => {
    const { data, error } = await supabase
      .from('knowledge')
      .select('category')
      .not('category', 'is', null)

    if (error) {
      throw new Error(`Failed to fetch category breakdown: ${error.message}`)
    }

    // Count categories
    const categoryCount: Record<string, number> = {}
    data?.forEach(item => {
      if (item.category) {
        categoryCount[item.category] = (categoryCount[item.category] || 0) + 1
      }
    })

    return Object.entries(categoryCount).map(([category, count]) => ({
      category: category as FAQCategory,
      count
    }))
  }

  const fetchRecentActivities = async (): Promise<RecentActivity[]> => {
    // Get recently created FAQs
    const { data: recentFAQs, error: recentError } = await supabase
      .from('knowledge')
      .select('id, question, status, created_at, updated_at, approved_at')
      .order('updated_at', { ascending: false })
      .limit(10)

    if (recentError) {
      throw new Error(`Failed to fetch recent activities: ${recentError.message}`)
    }

    const activities: RecentActivity[] = []

    recentFAQs?.forEach(faq => {
      const createdAt = new Date(faq.created_at)
      const updatedAt = new Date(faq.updated_at)
      const approvedAt = faq.approved_at ? new Date(faq.approved_at) : null

      // Determine the most recent significant action
      let activityType: RecentActivity['type'] = 'created'
      let activityTimestamp = faq.created_at
      let activityDescription = `FAQ "${faq.question.substring(0, 50)}..." wurde erstellt`

      if (approvedAt && approvedAt > updatedAt && approvedAt > createdAt) {
        activityType = 'approved'
        activityTimestamp = faq.approved_at!
        activityDescription = `FAQ "${faq.question.substring(0, 50)}..." wurde genehmigt`
      } else if (updatedAt > createdAt) {
        activityType = 'updated'
        activityTimestamp = faq.updated_at
        activityDescription = `FAQ "${faq.question.substring(0, 50)}..." wurde aktualisiert`
      }

      if (faq.status === 'archived') {
        activityType = 'archived'
        activityDescription = `FAQ "${faq.question.substring(0, 50)}..." wurde archiviert`
      }

      activities.push({
        id: faq.id,
        type: activityType,
        title: faq.question.substring(0, 60) + (faq.question.length > 60 ? '...' : ''),
        description: activityDescription,
        timestamp: activityTimestamp,
        status: faq.status as FAQStatus
      })
    })

    return activities.slice(0, 5) // Limit to 5 most recent
  }

  const fetchDashboardData = async () => {
    try {
      setDashboardData(prev => ({ ...prev, isLoading: true, error: null }))

      const [stats, categoryBreakdown, recentActivities] = await Promise.all([
        fetchDashboardStats(),
        fetchCategoryBreakdown(),
        fetchRecentActivities()
      ])

      setDashboardData({
        stats,
        categoryBreakdown,
        recentActivities,
        isLoading: false,
        error: null
      })
    } catch (error) {
      console.error('Error fetching dashboard data:', error)
      setDashboardData(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      }))
    }
  }

  const refreshData = () => {
    fetchDashboardData()
  }

  useEffect(() => {
    fetchDashboardData()

    // Set up real-time subscriptions
    const knowledgeSubscription = supabase
      .channel('knowledge-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'knowledge'
        },
        () => {
          // Refresh dashboard data when knowledge table changes
          fetchDashboardData()
        }
      )
      .subscribe()

    return () => {
      knowledgeSubscription.unsubscribe()
    }
  }, [])

  return {
    ...dashboardData,
    refreshData
  }
}